<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="500" @keydown.enter="onEnter" @keydown.esc="onEsc">
        <v-card class="mb-3">
            <v-card-title>
                <div class="d-flex justify-space-between">
                    <h4>Filters</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat"
                           @click="$emit('update:modelValue', false)"/>
                </div>
            </v-card-title>

            <v-card-text ref="filtersContainer">
                <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                         variant="tonal">
                    <p v-for="error in errors">{{ error }}</p>
                </v-alert>

                <div v-for="key in Object.keys(filters)" :key="key">
                    <!-- Render each filter based on the key -->
                    <Filter v-model="formData[key]" :filter="filters[key]" :index="key"/>
                </div>
            </v-card-text>

            <v-card-actions class="d-flex justify-space-between">
                <v-select
                    v-if="filterProfiles && filterProfiles.length"
                    v-model="selectedFilterProfile"
                    :items="filterProfiles"
                    class="pa-0"
                    clearable
                    density="compact"
                    hide-details
                    hide-no-data
                    item-title="label"
                    item-value="id"
                    label="Apply Saved Filter"
                    max-width="170"
                    return-object
                    variant="plain"
                    @update:modelValue="applyFilterProfile"
                />
                <v-btn :disabled="checkEmptyForm" size="small"
                       type="button"
                       @click="saveFilterProfile">
                    {{ selectedFilterProfile ? 'Update' : 'Save' }} Filter
                </v-btn>
                <v-btn :disabled="checkEmptyForm" size="small"
                       type="button"
                       @click="resetFilters">
                    Reset
                </v-btn>
                <v-btn :disabled="checkEmptyForm"
                       color="blue"
                       size="small" @click="applyFilters">Apply
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <Map-Dialog v-if="mapDialogSwitch" v-model="mapDialogSwitch" :setMapValues="mapValues" @mapValue="mapValue"/>

    <v-dialog v-model="filterProfileDialogSwitch" persistent width="400">
        <v-form class="mb-3" validate-on="input lazy"
                @submit.prevent="storeFilterProfile">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Add New Filter Profile</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="filterProfileErrors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in filterProfileErrors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="filterProfileFormData['label']"
                                :rules="[vRules.required]"
                                density="compact" label="Label"
                            />
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!filterProfileFormData['label']" color="primary" size="small" type="submit">Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {onMounted, onUnmounted, nextTick, reactive, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Filter from "@/components/cards/devices/Filter.vue";
import MapDialog from "@/components/cards/devices/MapDialog.vue";

const errors = ref([]);
const filtersContainer = ref(null);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    filters: Object,
});
const formData = reactive(props.filters);
const filterProfileFormData = reactive({
    label: null,
    configs: {}
});
const emits = defineEmits([
    "update:modelValue",
    "resetFilters",
    "applyFilters"
]);
const checkEmptyForm = ref(true);
const mapDialogSwitch = ref(false);
const filterProfiles = ref(null);
const filterProfileErrors = ref([]);
const selectedFilterProfile = ref(null);
const dialogSwitch = ref(props.modelValue);
const filterProfileDialogSwitch = ref(false);
const mapValues = ref({
    type: 0,
    center_lat: null,
    center_lon: null,
    radius: null,
});

const fetchFilterProfile = () => {
    send('get', 'filter-profiles?list=1')
        .then((response) => {
            filterProfiles.value = response.data.data;
        }).catch((error) => {
        snackbar.showSnackbar('Filter profiles could not be fetched', 'error')
    })
}
const saveFilterProfile = () => {
    if (checkEmptyForm.value) {
        alert("Add some filters to save");
        return;
    }

    if (selectedFilterProfile.value) {
        filterProfileFormData['label'] = selectedFilterProfile.value.label;
    }
    Object.entries(formData).forEach(([key, value]) => {
        if (value['data'] === null) return; // Skip if 'data' is not present

        filterProfileFormData['configs'][key] = filterProfileFormData['configs'][key] || {};

        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (value.hasOwnProperty(field) && value[field] !== null) {
                filterProfileFormData['configs'][key][field] = value[field];
            }
        });
    });

    filterProfileDialogSwitch.value = true;
}
const storeFilterProfile = () => {
    filterProfileErrors.value = [];
    if (filterProfileFormData['configs']) {
        send(selectedFilterProfile.value ? 'put' : 'post', `filter-profiles${selectedFilterProfile.value ? '/' + selectedFilterProfile.value.id : ''}`, filterProfileFormData)
            .then((response) => {
                snackbar.showSnackbar(response.data.message);
                if (response.data.success) {
                    if (selectedFilterProfile.value) {
                        filterProfiles.value.map((filterProfile, index) => {
                            if (filterProfile.id === selectedFilterProfile.value.id) {
                                filterProfiles.value[index] = response.data.data;
                            }
                        })
                    } else {
                        filterProfiles.value.push(response.data.data);
                    }
                    snackbar.showSnackbar(response.data.message);
                    filterProfileDialogSwitch.value = false;
                    filterProfileFormData['label'] = null;
                    filterProfileFormData['configs'] = {};
                }
            }).catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    filterProfileErrors.value.push(validationErrors[key][0]);
                });
                snackbar.showSnackbar('Something went wrong', 'error');
            }
        })
    }
}
const applyFilterProfile = () => {
    emits('resetFilters', false);
    if (selectedFilterProfile.value) {
        Object.entries(selectedFilterProfile.value.configs).forEach(([key, values]) => {
            if (props['filters'][key]) {
                Object.entries(values).forEach(([index, val]) => {
                    props['filters'][key][index] = val;
                })
                checkEmptyForm.value = false;
            }
        })
        applyFilters();
    }
}
const mapValue = (template) => {
    formData["geo_radius"]["latitude"] = template.center_lat;
    formData["geo_radius"]["longitude"] = template.center_lon;
    formData["geo_radius"]["data"] = template.radius;
}
const resetFilters = () => {
    selectedFilterProfile.value = null;

    emits('resetFilters');
    emits('update:modelValue', false);
}
const validateEmptyForm = (newFormData) => {
    checkEmptyForm.value = true;
    for (const [key, filter] of Object.entries(newFormData)) {
        if (filter?.data !== '' && filter?.data !== null) {
            checkEmptyForm.value = false;
        } else {
            filter.data = null;
        }
    }
}
const applyFilters = () => {
    errors.value = [];
    emits('update:modelValue', false);
}
const onEnter = () => {
    if (!checkEmptyForm.value) applyFilters();
}
const onEsc = () => {
    emits('update:modelValue', false);
}

watch(formData, (newFormData) => validateEmptyForm(newFormData), {deep: true});
onMounted(() => {
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    filterProfiles.value = localStorageProfiles?.profiles ?? null;
    if (filterProfiles.value && localStorageProfiles?.selectedProfile) {
        selectedFilterProfile.value = filterProfiles.value.find((item) => item.id === localStorageProfiles.selectedProfile);
    }
    if (!filterProfiles.value) {
        fetchFilterProfile();
    }
    validateEmptyForm(formData)
    nextTick(() => {
        setTimeout(() => {
            const container = filtersContainer.value?.$el ?? filtersContainer.value;
            const inputs = container?.querySelectorAll('input:not([readonly])') || [];
            const visibleInput = Array.from(inputs).find(el => el.offsetParent !== null);
            if (visibleInput) visibleInput.focus();
        }, 500)
    })
})
onUnmounted(() => {
    if (filterProfiles.value) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            profiles: filterProfiles.value,
            selectedProfile: selectedFilterProfile.value?.id ?? null
        }));
    }
    emits('applyFilters');
})
</script>
